import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { sitzungQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * **Claude mit Work verbinden** ([Z-21](../../../../docs/phasen/zusatz-21-claude-anbindung.md)).
 *
 * Die Adresse, die in claude.ai oder Claude Code eingetragen wird, und die Liste der eigenen
 * Verbindungen mit „Trennen". Wer freigeschaltet ist, steht in der Benutzerverwaltung (Häkchen
 * „🤖 Claude").
 */
export function ClaudeAnbindung() {
  const { data: sitzung } = useQuery(sitzungQuery)
  const queryClient = useQueryClient()
  const { data: verbindungen = [] } = useQuery({
    queryKey: ['claude', 'verbindungen'],
    queryFn: () => trpc.claude.verbindungen.query(),
  })
  const trennen = useMutation({
    mutationFn: (id: string) => trpc.claude.trennen.mutate({ id }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['claude', 'verbindungen'] }),
  })

  const adresse = `${window.location.origin}/mcp`

  return (
    <Card>
      <CardPad className="text-[12.5px]">
        <p className="text-ink2">
          Claude sieht nur Aufgaben, die mit „🤖 Für Claude freigeben" markiert sind, und kann dort
          einen Stand melden und Kommentare entwerfen. Abschicken kann nur ein Mensch.
        </p>

        <div className="mt-3 font-semibold">Adresse des Connectors</div>
        <div className="mt-1 flex items-center gap-1.5">
          <code className="rounded-lg bg-[#f3f2ef] px-2 py-1 font-mono text-[12px]">{adresse}</code>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void navigator.clipboard?.writeText(adresse)}
          >
            Kopieren
          </Button>
        </div>

        <ol className="mt-3 list-decimal space-y-1 pl-5 text-ink2">
          <li>
            <strong>claude.ai / Claude Desktop:</strong> Einstellungen → Connectors → Eigenen
            Connector hinzufügen → Name „NetCon Work", Adresse oben. Danach „Verbinden" – Work fragt
            einmal nach deiner Freigabe.
          </li>
          <li>
            <strong>Claude Code</strong> (mit den Repositories):{' '}
            <code className="font-mono text-[11.5px]">
              claude mcp add --transport http netcon-work {adresse}
            </code>
            , dann in Claude Code <code className="font-mono text-[11.5px]">/mcp</code> →
            „Authenticate".
          </li>
        </ol>

        {!sitzung?.claudeZugang && (
          <p className="mt-3 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[#8a6100]">
            Für dich selbst ist der Zugang noch aus – Häkchen „🤖 Claude" unter Team &amp; Welten →
            Benutzer setzen.
          </p>
        )}

        <div className="mt-4 font-semibold">Meine Verbindungen</div>
        {verbindungen.length === 0 ? (
          <p className="mt-1 text-muted">Noch keine.</p>
        ) : (
          <ul className="mt-1">
            {verbindungen.map((v) => (
              <li
                key={v.id}
                className="flex items-center gap-2 border-b border-line py-1.5 last:border-b-0"
              >
                <span className="font-semibold">{v.clientName}</span>
                <span className="text-[11.5px] text-muted">
                  seit {new Date(v.erstelltAm).toLocaleDateString('de-DE')}
                  {v.zuletztBenutzt
                    ? ` · zuletzt ${new Date(v.zuletztBenutzt).toLocaleString('de-DE')}`
                    : ''}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  disabled={trennen.isPending}
                  onClick={() => trennen.mutate(v.id)}
                >
                  Trennen
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardPad>
    </Card>
  )
}
